import { Filter, Texture } from "pixi.js";
import type { GameConfig } from "../config/GameConfig";
import type { EnvironmentPalette } from "../config/Palette";
import type { Divider } from "../entities/Divider";
import contourFrag from "./shaders/contour.frag?raw";
import contourVert from "./shaders/contour.vert?raw";

export class OrganicContourFilter {
  readonly filter: Filter;

  constructor(private readonly config: GameConfig) {
    this.filter = Filter.from({
      gl: { vertex: contourVert, fragment: contourFrag },
      resources: {
        uForceMap: Texture.WHITE.source,
        uDividerMap: Texture.WHITE.source,
        contourUniforms: {
          uTime: { value: 0, type: "f32" },
          uThreshold: { value: config.visual.metaballThreshold, type: "f32" },
          uContourWidth: { value: config.visual.contourWidth, type: "f32" },
          uDistortAmp: { value: config.visual.distortAmp, type: "f32" },
          uColorPulse: { value: config.visual.colorPulse, type: "f32" },
          uSwap: { value: 0, type: "f32" },
          uSeam: { value: 0, type: "f32" },
          uOval: { value: [0.5, 0.5, 0.42, 0.4], type: "vec4<f32>" },
          uResolution: { value: [1, 1], type: "vec2<f32>" },
          uUpperBg: { value: [...config.visual.upper.bg], type: "vec3<f32>" },
          uUpperFill: { value: [...config.visual.upper.fill], type: "vec3<f32>" },
          uUpperLine: { value: [...config.visual.upper.line], type: "vec3<f32>" },
          uUpperAccent: { value: [...config.visual.upper.accent], type: "vec3<f32>" },
          uLowerBg: { value: [...config.visual.lower.bg], type: "vec3<f32>" },
          uLowerFill: { value: [...config.visual.lower.fill], type: "vec3<f32>" },
          uLowerLine: { value: [...config.visual.lower.line], type: "vec3<f32>" },
          uLowerAccent: { value: [...config.visual.lower.accent], type: "vec3<f32>" },
        },
      },
    });
    this.filter.padding = 0;
    this.filter.blendMode = "normal";
  }

  attachMaps(forceMap: Texture, dividerMap: Texture): void {
    this.filter.resources.uForceMap = forceMap.source;
    this.filter.resources.uDividerMap = dividerMap.source;
  }

  sync(time: number, width: number, height: number, pixelRatio = 1, divider?: Divider): void {
    const u = this.filter.resources.contourUniforms.uniforms as Record<string, unknown>;
    const vis = this.config.visual;
    u.uTime = time;
    u.uThreshold = vis.metaballThreshold;
    u.uContourWidth = vis.contourWidth;
    u.uDistortAmp = vis.distortAmp;
    u.uColorPulse = vis.colorPulse;
    u.uSwap = vis.swapEnvironments ? 1 : 0;
    u.uSeam = divider?.isOval ? 1 : 0;
    const oval = (u.uOval as number[]) ?? [0.5, 0.5, 0.42, 0.4];
    const uv = divider?.uvOval() ?? [0.5, 0.5, 0.42, 0.4];
    oval[0] = uv[0];
    oval[1] = uv[1];
    oval[2] = uv[2];
    oval[3] = uv[3];
    (u.uResolution as number[])[0] = Math.max(1, width * pixelRatio);
    (u.uResolution as number[])[1] = Math.max(1, height * pixelRatio);
    this.writeRgb(u, "uUpper", vis.upper);
    this.writeRgb(u, "uLower", vis.lower);
  }

  private writeRgb(
    u: Record<string, unknown>,
    prefix: "uUpper" | "uLower",
    palette: EnvironmentPalette,
  ): void {
    this.copy3(u[`${prefix}Bg`] as number[], palette.bg);
    this.copy3(u[`${prefix}Fill`] as number[], palette.fill);
    this.copy3(u[`${prefix}Line`] as number[], palette.line);
    this.copy3(u[`${prefix}Accent`] as number[], palette.accent);
  }

  private copy3(target: number[], rgb: readonly [number, number, number]): void {
    target[0] = rgb[0];
    target[1] = rgb[1];
    target[2] = rgb[2];
  }
}
